import React, { useState, useMemo } from 'react';
import { ExternalLink, Copy, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const STATUS_BADGE = {
  PR1: 'bg-[#2C853C] text-white',
  PR2: 'bg-[#FFA500] text-white',
  PR3: 'bg-[#DC143C] text-white',
};

const DECISION_COLORS = {
  'For Review':      'bg-[#FFA500] text-white',
  'Approved':        'bg-[#2C853C] text-white',
  'Failed':          'bg-[#DC143C] text-white',
  'Excluded':        'bg-[#4169E1] text-white',
  'Suggest Approve': 'bg-[#6D4694] text-white',
  'Suggest Exclude': 'bg-[#6D4694] text-white',
  'Suggest Fail':    'bg-[#6D4694] text-white',
};

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  if (!text) return null;
  const handleCopy = (e) => {
    e.preventDefault();
    navigator.clipboard.writeText(text ?? '');
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button onClick={handleCopy} className="shrink-0 text-gray-400 hover:text-blue-600 transition-colors" title="Copy URL">
      {copied ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
    </button>
  );
};

const ensureHttps = (url) => {
  if (!url) return url;
  if (/^https?:\/\//i.test(url)) return url;
  return `https://${url}`;
};

const COLUMNS = [
  { key: 'ppf_file_name',        label: 'POLICY',       width: '160px' },
  { key: 'ppf_url',         label: 'PPU',           width: '200px' },
  { key: 'scraper_date',    label: 'SCRAPED',       width: '110px' },
  { key: 'status',          label: 'STATUS',        width: '90px'  },
  { key: 'decision',        label: 'DECISION',      width: '120px' },
  { key: 'ai_version',      label: 'AI VER.',       width: '80px'  },
  { key: 'assessment_date', label: 'ASSESS DATE',   width: '110px' },
];

export const PoliciesTable = ({ data = [], total, onDecisionClick, tableHeight = '750px' }) => {
  const navigate = useNavigate();
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return data;
    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key] ?? '';
      const bValue = b[sortConfig.key] ?? '';
      if (typeof aValue === 'string') {
        return sortConfig.direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      } else {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
    });
  }, [data, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  return (
    <div>
      <div className="mb-2">
        <h3 className="text-base font-semibold text-gray-900">Privacy Policy Documents</h3>
        <p className="text-sm text-gray-500">
          Privacy policy documents and assessment details ({(total ?? data.length).toLocaleString()} total)
        </p>
      </div>
      <div
        className="overflow-y-auto border rounded-lg"
        style={{ maxHeight: tableHeight }}
      >
      <table className="w-full">
        <thead className="bg-gray-50 sticky top-0 z-10">
          <tr className="border-b border-gray-200">
            {COLUMNS.map(col => (
              <th
                key={col.key}
                className="relative text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-2 border-r border-gray-200 last:border-r-0"
                style={{ width: col.width }}
              >
                {col.label}
                <svg className="absolute bottom-1 right-1 opacity-70" width="12" height="12" viewBox="0 0 12 12">
                  <line x1="11" y1="1" x2="1" y2="11" stroke="#374151" strokeWidth="1.5"/>
                  <line x1="11" y1="5" x2="5" y2="11" stroke="#374151" strokeWidth="1.5"/>
                  <line x1="11" y1="9" x2="9" y2="11" stroke="#374151" strokeWidth="1.5"/>
                </svg>
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {data.length === 0 ? (
            <tr>
              <td colSpan={COLUMNS.length} className="px-4 py-8 text-center text-gray-400">No documents found</td>
            </tr>
          ) : (
            sortedData.map(row => (
              <tr key={row.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-4 px-2 font-mono text-xs" style={{ width: '160px' }}>
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => navigate(`/policy?policy=${encodeURIComponent(row.ppf_checksum)}&ppf_file=${encodeURIComponent(row.ppf_file_name)}`)}
                      className="text-blue-600 hover:underline text-left truncate"
                      title={row.ppf_file_name}
                    >
                      {row.ppf_file_name}
                    </button>
                    <button
                      onClick={() => window.open(`/policy?policy=${encodeURIComponent(row.ppf_checksum)}&ppf_file=${encodeURIComponent(row.ppf_file_name)}`, '_blank')}
                      className="text-gray-400 hover:text-blue-600 transition-colors shrink-0"
                      title="Open in new tab"
                    >
                      <ExternalLink className="w-3 h-3" />
                    </button>
                  </div>
                </td>

                <td className="py-4 px-2" style={{ width: '200px' }}>
                  <div className="flex items-center gap-1 max-w-[200px]">
                    <a
                      href={ensureHttps(row.ppf_url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline text-xs font-mono truncate"
                      title={row.ppf_url}
                    >
                      {row.ppf_url}
                    </a>
                    <CopyButton text={row.ppf_url} />
                  </div>
                </td>

                <td className="py-4 px-2 text-gray-700 text-sm whitespace-nowrap" style={{ width: '110px' }}>
                  {row.scraper_date?.split('T')[0]}
                </td>

                <td className="py-4 px-2" style={{ width: '90px' }}>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_BADGE[row.status] || 'bg-gray-100 text-gray-700'}`}>
                    {row.status}
                  </span>
                </td>

                <td
                  className="py-4 px-2 whitespace-nowrap cursor-pointer"
                  style={{ width: '120px' }}
                  onClick={() => onDecisionClick?.(row)}
                  title="Click to update decision"
                >
                  <span className={`inline-flex items-center gap-0.5 px-2 py-0.5 rounded text-xs font-medium ${DECISION_COLORS[row.decision] ?? 'bg-gray-100 text-gray-700'}`}>
                    {row.decision ?? '—'}
                    {row.decision_changed === 1 && <span className="font-bold">*</span>}
                  </span>
                </td>

                <td className="py-4 px-2 text-gray-700 text-sm" style={{ width: '80px' }}>
                  {row.ai_version}
                </td>

                <td className="py-4 px-2 text-gray-700 text-sm whitespace-nowrap" style={{ width: '110px' }}>
                  {row.assessment_date}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      </div>
    </div>
  );
};
